// Heuristic section detection for medical manuscripts.
//
// A heading is a short standalone line that, after stripping an optional
// enumerator ("2.", "II.", "A)", "1 |") and trailing ":"/".", matches an
// IMRaD / back-matter landmark (case-insensitive). Back-matter
// (Acknowledgements / Funding / Conflicts / Ethics / Data availability /
// Supplementary / Appendix) is detected so the References span is correctly
// bounded and never ingests it as a reference. The contract is the
// SectionSpan[] shape, not the heuristic.

import type { SectionName, SectionSpan } from './types';

interface HeaderPattern {
  section: SectionName;
  patterns: RegExp[];
}

const HEADER_PATTERNS: HeaderPattern[] = [
  { section: 'abstract', patterns: [/^(abstract|summary|structured abstract)$/i] },
  { section: 'introduction', patterns: [/^(introduction|background|background and objectives?)$/i] },
  {
    section: 'methods',
    patterns: [
      /^(methods?|methodology|methods? and materials?)$/i,
      /^(materials?\s*(?:and|&)\s*methods?)$/i,
      /^((?:patients?|subjects?|participants?|cases?)\s*(?:and|&)\s*methods?)$/i,
      /^(study\s+design(?:\s+and\s+(?:setting|methods?))?)$/i,
      /^(design\s+and\s+methods?)$/i,
    ],
  },
  {
    section: 'results',
    // Combined "Results and Discussion" maps to results so the span exists.
    patterns: [/^(results?(?:\s*(?:and|&)\s*discussion)?|findings|main findings)$/i],
  },
  { section: 'discussion', patterns: [/^(discussion|comment|comments)$/i] },
  {
    section: 'conclusions',
    patterns: [
      /^(conclusions?|concluding remarks?|conclusions? and recommendations?|summary and conclusions?)$/i,
    ],
  },
  {
    section: 'references',
    patterns: [/^(references?|bibliography|works\s+cited|literature\s+cited)$/i],
  },
  {
    // Back-matter — not analysed, but a boundary so References ends here.
    section: 'appendix',
    patterns: [
      /^(acknowledge?ments?)$/i,
      /^(funding|funding (?:information|statement|source)s?|financial support|grant support|sources? of funding)$/i,
      /^(conflicts?\s+of\s+interest(?:\s+statement)?|competing\s+interests?|declarations?(?:\s+of\s+(?:competing\s+)?interests?)?|disclosures?(?:\s+statement)?)$/i,
      /^(authors?'?\s+contributions?|authorship|contributorship)$/i,
      /^(data\s+availability(?:\s+statement)?|availability of data(?:\s+and\s+materials?)?|code availability)$/i,
      /^(ethic(?:s|al)(?:\s+(?:approval|statement|considerations?|committee))?|institutional review board|consent(?:\s+(?:to participate|for publication))?)$/i,
      /^(supplement(?:ary|al)(?:\s+(?:material|data|information|file)s?)?|supporting information|appendix|appendices|abbreviations|list of abbreviations|orcid)$/i,
    ],
  },
];

// Optional leading enumerator: "2." "2)" "II." "A -" "1 |" "Section 2:".
const ENUMERATOR_RE =
  /^\s*(?:section\s+)?(?:\d{1,2}|[IVXLC]{1,5}|[A-Z])\s*[.)\]|:–-]\s+/i;

function headingCandidates(trimmed: string): string[] {
  const out = new Set<string>();
  // Strip trailing heading punctuation ("Methods:", "Methods.", "Methods —").
  const base = trimmed.replace(/[\s.:•·—–-]+$/u, '').trim();
  out.add(base);
  // Strip a leading enumerator ("2. Methods", "II. Methods", "A) Results").
  const noEnum = base.replace(ENUMERATOR_RE, '').trim();
  if (noEnum && noEnum !== base) out.add(noEnum);
  // NOTE: deliberately NO "split on first colon" candidate — an inline
  // "Funding: this was funded by…" / "Background: …" sentence must not be
  // mistaken for a section heading. A genuine "Heading:" line is already
  // covered by the trailing-punctuation strip above.
  return [...out].filter((s) => s.length > 0);
}

interface HeaderMatch {
  index: number;
  heading: string;
  section: SectionName;
}

function findHeaders(rawText: string): HeaderMatch[] {
  const matches: HeaderMatch[] = [];
  const lines = rawText.split('\n');
  let cursor = 0;
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.length > 0 && trimmed.length <= 80) {
      const candidates = headingCandidates(trimmed);
      outer: for (const { section, patterns } of HEADER_PATTERNS) {
        for (const p of patterns) {
          if (candidates.some((cand) => p.test(cand))) {
            matches.push({ index: cursor, heading: trimmed, section });
            break outer;
          }
        }
      }
    }
    cursor += line.length + 1; // +1 for the '\n'
  }
  return matches;
}

export function detectSections(rawText: string): SectionSpan[] {
  const matches = findHeaders(rawText);
  if (matches.length === 0) return [];

  const spans: SectionSpan[] = [];
  for (let i = 0; i < matches.length; i++) {
    const current = matches[i];
    const next = matches[i + 1];
    spans.push({
      heading: current.heading,
      section: current.section,
      start: current.index,
      end: next ? next.index : rawText.length,
    });
  }
  return spans;
}

export function sectionAt(spans: SectionSpan[], offset: number): SectionName {
  for (const s of spans) {
    if (offset >= s.start && offset < s.end) return s.section;
  }
  return 'unknown';
}

// Lines that are never the manuscript title (journal banner, EM/markup
// scaffolding, running heads, identifiers).
const NON_TITLE_RE =
  /^(oman medical journal|manuscript|original article|review article|case report|short communication|editorial|--|abstract|keywords?|running (?:head|title)|doi\b|https?:\/\/|www\.|page \d+|\d+$|table \d+|figure \d+)/i;

/** Best-guess title: first substantial line before the first section that
 *  isn't obvious journal/markup scaffolding. */
export function detectTitle(rawText: string, spans: SectionSpan[]): string {
  const limit = spans.length > 0 ? spans[0].start : Math.min(rawText.length, 2000);
  const head = rawText.slice(0, limit);
  let firstPlausible = '';
  for (const line of head.split('\n')) {
    const t = line.trim();
    if (t.length < 10 || t.length > 250) continue;
    if (NON_TITLE_RE.test(t)) continue;
    if (!/[a-z]/i.test(t)) continue; // need letters
    return t;
  }
  // Fallback: original heuristic (first 10–250 char line) if nothing passed.
  for (const line of head.split('\n')) {
    const t = line.trim();
    if (t.length >= 10 && t.length <= 250) {
      firstPlausible = t;
      break;
    }
  }
  return firstPlausible;
}
